import { createRouter, createWebHistory } from "vue-router";
import store from "@/store/index";
import BaseService from "@/services/base.service";
import { staticDebtorRoutes, debtorWvpRoutes } from "@/router/debtor.js";
import { clerkDebtorRoutes, clerkCorporateRoutes, clerkDebtorEditRoutes } from "@/router/clerk.js";
import { adminRoutes } from "@/router/admin.js";
import { corporateRoutes } from "@/router/corporate_new.js";
import { useUiStore } from "@/store/uiStore";
import useStopImpersonation from "@/composables/useStopImpersonation";


const routes = [
  staticDebtorRoutes,
  adminRoutes,
  clerkDebtorRoutes,
  clerkCorporateRoutes,
  clerkDebtorEditRoutes,
  debtorWvpRoutes,
  corporateRoutes,
  {
    path: "/thank-you",
    name: "thank-you",
    component: () => import("../views/ThankYou.vue"),
    meta: {
      requiresAuth: true,
      role: "debtor,corp_debtor",
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("../views/LoginView.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/pages/404.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (store.state.activeCase?.scrollY) {
      return { top: store.state.activeCase?.scrollY }
    }
    return { top: 0 };
  },
});

const isItemAfterCurrentState = (currentState, route) => {
  const currentStateIndex = router
    .getRoutes()
    .findIndex((i) => i.name === currentState);
  const routeIndex = router.getRoutes().findIndex((i) => i.name === route.name);
  return routeIndex >= currentStateIndex;
};

const corporateInterceptor = (to, from) => {
  if (store.getters.corporateCaseData) {
    if (isItemAfterCurrentState(store.getters.corporateCaseCurrentState, to)) {
      const currentState = to.name;
      BaseService.put(
        `/corp/cases/${store.getters.userData.corpCaseId}`,
        { currentState: currentState }
      )
        .then(() => store.commit("setCorporateCurrentState", currentState))
        .catch((err) => console.log(err));
    }
  }
};

router.afterEach(async (to, from) => {
  if (to.name === 'login') {
    return;
  }
  if (store.getters.userData?.role === 'corp_debtor') {
    corporateInterceptor(to, from);
    await store.dispatch('retrieveCorporateCaseData');
  }
})

router.beforeEach(async (to, from, next) => {

  const token = sessionStorage.getItem("token");
  const userData = JSON.parse(sessionStorage.getItem("userData"));

  if (userData) {
    store.commit('setUserData', userData);
  }

  if (to.matched.some((record) => record.meta?.requiresAuth)) {
    if (userData?.role === 'clerk' && to.meta.impersonation === false) {
      await useStopImpersonation();
    }

    if (userData?.role === 'clerk' && to.matched.findIndex(item => item.name === 'debtor-view-edit-case') !== -1) {
      if (to.params.id) {
        store.commit("setDebtorViewEditCaseId", to.params.id);
      }
      store.dispatch("retrieveCaseData");
      store.dispatch("retrieveFrontendState");
      next();
    } else {
      //Validate navigation - if data was already sent -> user is always allowd to navigate
      if (store.getters.caseData?.dataSent) {
        store.commit("setFormValid");
        store.commit("setInteractionStateValid");
      } else {
        if (to.meta.requiresInternalValidation) {
          store.commit("setFormInvalid");
        } else {
          store.commit("setFormValid");
        }
        if (to.meta.requiresInteraction) {
          store.commit("setInteractionStateInvalid");
        } else {
          store.commit("setInteractionStateValid");
        }
      }

      if (
        store.getters.userData?.role === "debtor" &&
        store.getters.userData &&
        store.getters.frontendState &&
        store.getters.frontendState.currentState
      ) {
        if (
          from.meta?.previous !== to.name &&
          isItemAfterCurrentState(store.getters.frontendState.currentState, to)
        ) {
          const data = { currentState: to.name };
          if (
            store.getters.userData.wvpYear > 0 &&
            store.getters.userData.wvpCaseId > 0
          ) {
            BaseService.put(
              `/cases/${store.getters.wvpCaseData.caseId}/wvp-frontend-state/${store.getters.wvpCaseData.id}`,
              data,
            )
              .then(() => store.commit("setCurrentState", data.currentState))
              .catch((err) => console.log(err));
          } else {
            BaseService.updateFrontendState(
              store.getters.userData.caseId,
              data,
            ).then((res) => store.commit("setCurrentState", data.currentState));
          }
        }
      }

      if (store.getters.userData?.role === "clerk") {
        if (store.state.clerkData?.cases.length === 0)
          store.dispatch("retrieveClerkData");
      }

      if (!token) {
        next({
          path: "/login",
        });
      } else {
        if (!store.getters.userData?.role) {
          await store.dispatch("retrieveUserData");
          if (store.getters.role === "debtor") {
            if (
              store.getters.userData.wvpCaseId &&
              store.getters.userData.wvpYear
            ) {
              if (!store.getters.wvpCaseData) {
                store.dispatch("retrieveWvpCaseData");
                store.dispatch("retrieveWvpFrontendState");
              }
            } else {
              if (!store.getters.caseData) {
                store.dispatch("retrieveCaseData");
                store.dispatch("retrieveFrontendState");
              }
            }
          }
        }

        if (!to.meta.role.includes(store.getters.role)) {
          next({
            path: "/login",
          });
        } else {
          next();
        }
      }
    }
  } else {
    if (store.getters.userData?.role === "debtor" && !useUiStore().routes?.length) {
      await store.dispatch("retrieveCaseData");
      await store.dispatch("retrieveFrontendState");
      next({ path: to.fullPath, replace: true });
    } else {
      next();
    }
  }
});

export default router;
